import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, X, ArrowUpDown, Loader2 } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { ArticleCard } from '@/components/article/ArticleCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { postsApi, categoriesApi, tagsApi } from '@/lib/api';

const YazilarPage = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [postsData, catsData, tagsData] = await Promise.all([
        postsApi.getPublicPosts({ limit: 100 }),
        categoriesApi.getPublicCategories(),
        tagsApi.getPublicTags(),
      ]);
      setPosts(postsData.posts);
      setCategories(catsData);
      setTags(tagsData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort posts
  const filteredPosts = useMemo(() => {
    let result = [...posts];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(post =>
        post.title.toLowerCase().includes(query) ||
        post.excerpt.toLowerCase().includes(query) ||
        post.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Category filter
    if (selectedCategories.length > 0) {
      result = result.filter(post => selectedCategories.includes(post.category));
    }

    // Tag filter
    if (selectedTags.length > 0) {
      result = result.filter(post =>
        post.tags?.some(tag => selectedTags.includes(tag))
      );
    }

    // Sort
    const getSortDate = (post) => new Date(post.published_at || post.created_at || post.date || 0);

    if (sortBy === 'newest') {
      result.sort((a, b) => getSortDate(b) - getSortDate(a));
    } else if (sortBy === 'oldest') {
      result.sort((a, b) => getSortDate(a) - getSortDate(b));
    }

    return result;
  }, [posts, searchQuery, selectedCategories, selectedTags, sortBy]);

  const toggleCategory = (categorySlug) => {
    setSelectedCategories(prev =>
      prev.includes(categorySlug)
        ? prev.filter(c => c !== categorySlug)
        : [...prev, categorySlug]
    );
  };

  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategories([]);
    setSelectedTags([]);
    setSortBy('newest');
  };

  const hasActiveFilters = searchQuery || selectedCategories.length > 0 || selectedTags.length > 0;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <section className="container-wide py-12 sm:py-16">
        <div className="max-w-2xl">
          <span className="caption text-accent mb-2 block">Arşiv</span>
          <h1 className="heading-article text-foreground mb-4">Yazılar</h1>
          <p className="body-large text-muted-foreground">
            Taktik analizler, oyun modeli incelemeleri ve modern futbolun stratejik derinlikleri.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="container-wide pb-8">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          {/* Search */}
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Yazılarda ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-background border-border"
            />
          </div>

          <div className="flex items-center gap-3">
            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-36 bg-background border-border">
                <ArrowUpDown className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">En Yeni</SelectItem>
                <SelectItem value="oldest">En Eski</SelectItem>
              </SelectContent>
            </Select>

            {/* Filter Toggle */}
            <Button
              variant={showFilters ? "default" : "outline"}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? "bg-primary text-primary-foreground" : "border-border"}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtreler
              {hasActiveFilters && (
                <span className="ml-2 w-5 h-5 rounded-full bg-accent text-accent-foreground text-xs flex items-center justify-center">
                  {selectedCategories.length + selectedTags.length + (searchQuery ? 1 : 0)}
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="mt-6 p-6 bg-card border border-border rounded-sm animate-fade-in">
            {/* Categories */}
            <div className="mb-6">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-3">
                Kategoriler
              </h3>
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category._id}
                    onClick={() => toggleCategory(category.slug)}
                    className={`tag ${selectedCategories.includes(category.slug) ? 'tag-active' : ''}`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Tags */}
            <div className="mb-6">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-3">
                Etiketler
              </h3>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {tags.map((tag) => (
                  <button
                    key={tag._id}
                    onClick={() => toggleTag(tag.slug)}
                    className={`tag ${selectedTags.includes(tag.slug) ? 'tag-active' : ''}`}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4 mr-2" />
                Filtreleri Temizle
              </Button>
            )}
          </div>
        )}
      </section>

      {/* Horizontal Rule */}
      <div className="container-wide">
        <div className="h-px bg-border" />
      </div>

      {/* Posts List */}
      <section className="container-wide py-12">
        {filteredPosts.length > 0 ? (
          <>
            <p className="text-sm text-muted-foreground mb-8">
              {filteredPosts.length} yazı bulundu
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
              {filteredPosts.map((post) => (
                <ArticleCard key={post._id} post={post} />
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <p className="text-muted-foreground mb-4">
              {posts.length === 0 
                ? 'Henüz yayınlanmış yazı bulunmuyor.'
                : 'Arama kriterlerinize uygun yazı bulunamadı.'}
            </p>
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters}>
                Filtreleri Temizle
              </Button>
            )}
          </div>
        )}
      </section>
    </Layout>
  );
};

export default YazilarPage;
